import React from "react";
import { Chip, CircularProgress, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SyncIcon from "@mui/icons-material/Sync";
import VerifiedIcon from "@mui/icons-material/Verified";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import BlockIcon from "@mui/icons-material/Block";
import ErrorIcon from "@mui/icons-material/Error";
import { AccountStatus, AccountStatusLabel, AccountStatusType } from "../../Enum/AccountStatus";

// ─── Types ────────────────────────────────────────────────────────────────────

interface BadgeStyle {
    bg: string;
    border: string;
    text: string;
    iconColor: string;
}

interface StatusConfig {
    style: BadgeStyle;
    Icon: React.ComponentType<{ style?: React.CSSProperties }>;
}

// ─── Config par statut ────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<AccountStatusType, StatusConfig> = {
    [AccountStatus.ACTIVE]: {
        style: { bg: '#ff724f', border: '#6ee7b7', text: '#065f46', iconColor: '#10b981' },
        Icon: CheckCircleIcon,
    },
    [AccountStatus.CONTACT_COMPLETE]: {
        style: { bg: '#ff724f1a', border: '#ff724f', text: '#ff724f', iconColor: '#ff724f' },
        Icon: AssignmentTurnedInIcon,
    },
    [AccountStatus.INVITATION_PENDING]: {
        style: { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', iconColor: '#3b82f6' },
        Icon: ForwardToInboxIcon,
    },
    [AccountStatus.PENDING]: {
        style: { bg: '#f5f3ff', border: '#ddd6fe', text: '#4c1d95', iconColor: '#8b5cf6' },
        Icon: HourglassEmptyIcon,
    },
    [AccountStatus.APPAREILLAGE_EC]: {
        style: { bg: '#fefce8', border: '#fde68a', text: '#713f12', iconColor: '#eab308' },
        Icon: SyncIcon,
    },
    [AccountStatus.APPAREILLAGE_VALIDE]: {
        style: { bg: '#ecfdf5', border: '#a7f3d0', text: '#064e3b', iconColor: '#059669' },
        Icon: VerifiedIcon,
    },
    [AccountStatus.APPAREILLAGE_NF]: {
        style: { bg: '#fdf4ff', border: '#e9d5ff', text: '#581c87', iconColor: '#a855f7' },
        Icon: SearchOffIcon,
    },
    [AccountStatus.MANDAT_ECHU]: {
        style: { bg: '#fff7ed', border: '#fed7aa', text: '#7c2d12', iconColor: '#f97316' },
        Icon: EventBusyIcon,
    },
    [AccountStatus.PARTICULIER_BLOQUE]: {
        style: { bg: '#fff1f2', border: '#fecdd3', text: '#881337', iconColor: '#f43f5e' },
        Icon: BlockIcon,
    },
    [AccountStatus.ERROR]: {
        style: { bg: '#fef2f2', border: '#fecaca', text: '#7f1d1d', iconColor: '#ef4444' },
        Icon: ErrorIcon,
    },
};

// ─── Composant ────────────────────────────────────────────────────────────────

interface StatusBadgeProps {
    id: number;
    status: AccountStatusType;
    description: string;
    showLabel?: boolean;
    laod?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

export function StatusBadge({
    id ,
    status,
    description,
    showLabel = false,
    laod,
    className,
    style: styleProp,
}: StatusBadgeProps): JSX.Element | null {
    const config = STATUS_CONFIG[status];
    if (!config) return null;

    const { style: s, Icon } = config;
    return (
        <Tooltip title={description} placement="top" arrow>

            <Chip
                className={className}
                icon={<Icon style={{ color: s.iconColor, fontSize: 15 }} />}
                label={laod && id ? <CircularProgress aria-label="Loading…" size={15} sx={{ width: '20px' }} /> : AccountStatusLabel[status]}
                size="small"
                sx={{
                    bgcolor: s.bg,
                    border: `1px solid ${s.border}`,
                    color: s.text,
                    fontSize: '11px',
                    fontWeight: 500,
                    fontFamily: 'ui-monospace, "SFMono-Regular", Menlo, monospace',
                    '& .MuiChip-icon': { color: s.iconColor },
                    ...styleProp,
                }}
            />

        </Tooltip>
    );
}
